import { z } from "zod";

import { coarseLocationSchema } from "../../features/location/coarseLocation";
import { HORN_IDS, TRAFFIC_STATES, VEHICLE_PRESETS } from "../../types/traffic";
import type {
  HeartbeatInput,
  HeartbeatResponse,
  JoinInput,
  JoinResponse,
  TrafficApi,
} from "./TrafficApi";

const trafficSchema = z.object({
  activeDriverCount: z.number().int().nonnegative(),
  targetSpeedMph: z.number().positive(),
  state: z.enum(TRAFFIC_STATES),
  snapshotAt: z.iso.datetime(),
});

const driverSchema = z.object({
  publicDriverId: z.string().min(8),
  plate: z.string().min(1).max(7),
  vehiclePreset: z.enum(VEHICLE_PRESETS),
  colorId: z.string().min(3).max(24),
  hornId: z.enum(HORN_IDS),
  badge: z.enum(["garage", "founding"]).nullable(),
  garageOwned: z.boolean(),
  odometerMiles: z.number().nonnegative(),
});

const realtimeModeSchema = z.enum(["full", "reduced", "aggregate", "offline"]);

const joinResponseSchema = z.object({
  sessionId: z.string().min(8),
  driver: driverSchema,
  location: coarseLocationSchema.nullable(),
  traffic: trafficSchema,
  cohort: z.object({
    id: z.string().min(1),
    channel: z.string().min(1),
  }),
  serverTime: z.iso.datetime(),
  heartbeatIntervalSeconds: z.number().int().positive(),
  configurationRevision: z.number().int().positive(),
  features: z.object({
    realtimeMode: realtimeModeSchema,
    garageEnabled: z.boolean(),
    customPlatesEnabled: z.boolean(),
    sharingEnabled: z.boolean(),
  }),
}) satisfies z.ZodType<JoinResponse>;

const heartbeatResponseSchema = z.object({
  traffic: trafficSchema,
  configurationRevision: z.number().int().positive(),
  realtimeMode: realtimeModeSchema,
  heartbeatIntervalSeconds: z.number().int().positive(),
  sessionDistanceMiles: z.number().nonnegative(),
  cohortSessionIds: z.array(z.string().min(8)).optional(),
}) satisfies z.ZodType<HeartbeatResponse>;

export class SessionExpiredError extends Error {}

export class NetlifyTrafficApi implements TrafficApi {
  public async join(input: JoinInput = {}): Promise<JoinResponse> {
    return joinResponseSchema.parse(
      await this.request("/api/session/join", input),
    );
  }

  public async heartbeat(input: HeartbeatInput): Promise<HeartbeatResponse> {
    try {
      return heartbeatResponseSchema.parse(
        await this.request("/api/session/heartbeat", input),
      );
    } catch (error) {
      if (error instanceof HttpResponseError && error.status === 409) {
        throw new SessionExpiredError("Session expired.");
      }
      throw error;
    }
  }

  public async leave(sessionId: string): Promise<void> {
    await this.request("/api/session/leave", { sessionId }, true);
  }

  private async request(
    path: string,
    body: unknown,
    keepalive = false,
  ): Promise<unknown> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8_000);
    try {
      const response = await fetch(path, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        keepalive,
        signal: controller.signal,
      });
      const value: unknown = await response.json().catch(() => null);
      if (!response.ok) throw new HttpResponseError(response.status, value);
      return value;
    } finally {
      window.clearTimeout(timeout);
    }
  }
}

class HttpResponseError extends Error {
  public constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`Traffic API request failed with status ${status}.`);
  }
}
